/**
 * AirGPU Pricing Utilities
 * Cost calculation for GPU machine usage
 */

import type { AirGPUInstanceType, AirGPURegion, AirGPUProvisioningType } from './types';

// ============================================
// PRICING CONFIGURATION
// ============================================

/**
 * Base hourly rates per instance type (EUR), before the regional multiplier,
 * bandwidth and storage adjustments below.
 *
 * This table IS the price: billing (cron, billing-on-view, stop) charges from
 * it, and the storefront reads it too -- `GPUProduct.basePrice` in
 * lib/data/gpu-products.ts and STARTING_HOURLY_RATE are derived from it, never
 * copied. Change a rate here and every advertised price follows.
 */
export const BASE_HOURLY_RATES: Record<AirGPUInstanceType, {
  onDemand: number;
  spot: number; // Approximate spot pricing (typically 60-80% of on-demand)
}> = {
  'g4dn.xlarge': {
    onDemand: 0.65, // Tesla T4, half the vCPU/RAM of the 2xlarge.
    spot: 0.24,     // ~37% of on-demand, matching the 2xlarge ratio
  },
  'g4dn.2xlarge': {
    onDemand: 0.94, // Tesla T4
    spot: 0.35,     // ~37% of on-demand
  },
  'g5.2xlarge': {
    onDemand: 1.52, // A10G
    spot: 0.60,     // ~39% of on-demand
  },
  'g6.2xlarge': {
    onDemand: 1.20, // L4 - Customer price in EUR (AirGPU cost: ~$0.90/hr USD)
    spot: 0.48,     // ~40% of on-demand
  },
  'g6.4xlarge': {
    onDemand: 1.80, // L4 with more CPU/RAM
    spot: 0.72,     // ~40% of on-demand
  },
};

/**
 * Lowest on-demand base rate we sell - the "from" price quoted across the
 * site. Derived so the marketing floor follows the rate table automatically.
 */
export const STARTING_HOURLY_RATE = Math.min(
  ...Object.values(BASE_HOURLY_RATES).map((rates) => rates.onDemand)
);

/**
 * Regional pricing multipliers
 * Some regions have higher costs due to infrastructure/demand
 * Source: AirGPU API documentation
 */
export const REGIONAL_MULTIPLIERS: Record<AirGPURegion, number> = {
  'us-east-1': 1.00,      // Base region
  'us-east-2': 1.00,
  'us-west-1': 1.05,
  'us-west-2': 1.00,
  'eu-west-1': 1.08,
  'eu-west-2': 1.10,
  'eu-central-1': 1.08,
  'eu-north-1': 1.10,
  'ap-northeast-1': 1.15, // Tokyo - higher demand
  'ap-northeast-2': 1.12,
  'ap-southeast-1': 1.10,
  'ap-southeast-2': 1.15,
  'ap-south-1': 1.08,
  'ap-east-1': 1.12,      // Hong Kong
  'ca-central-1': 1.05,
  'af-south-1': 1.18,
};

/**
 * Upload bandwidth pricing adjustments per Mbit/s tier
 */
export const UPLOAD_BANDWIDTH_ADJUSTMENTS: Record<number, number> = {
  7: 0,      // Base
  15: 0.05,  // +€0.05/hour
  25: 0.12,  // +€0.12/hour
  45: 0.25,  // +€0.25/hour
};

/**
 * Storage included in every machine's hourly rate (GB).
 */
export const INCLUDED_VOLUME_GB = 100;

/**
 * Volume size pricing (per GB above INCLUDED_VOLUME_GB). Like the rest of the
 * hourly rate, it is only charged while the machine is running. The storage
 * copy on /pricing and /faq quotes these two constants.
 */
export const VOLUME_PRICING_PER_GB = 0.002; // €0.002/hour per GB

/**
 * Minimum balance buffer in hours required to start/create a machine.
 * This is a protection against abuse - users must have enough balance
 * for at least this many hours of usage before starting a machine.
 * Users can stop anytime and only pay for actual usage (per-minute billing).
 */
export const MINIMUM_BALANCE_BUFFER_HOURS = 3;

// ============================================
// PRICING CALCULATIONS
// ============================================

/**
 * Looks up the base rates for an instance type.
 *
 * Throws a descriptive error for a type with no rate instead of letting the
 * caller hit a TypeError on `undefined` (or, for inherited keys such as
 * "constructor", silently compute NaN). Billing, stop and the machine pages
 * all price from this table, so a machine row whose type has been removed
 * from it cannot be priced: never delete a rate while a live machine still
 * uses that type.
 */
function getBaseRates(instanceType: AirGPUInstanceType): { onDemand: number; spot: number } {
  if (!Object.hasOwn(BASE_HOURLY_RATES, instanceType)) {
    throw new Error(`No hourly rate configured for instance type "${instanceType}"`);
  }
  return BASE_HOURLY_RATES[instanceType];
}

/**
 * Regional multiplier for a region, or 1.0 for a region without one. Never
 * throws: an unknown region prices at the base rate rather than blocking
 * billing or a stop.
 */
export function getRegionalMultiplier(region: AirGPURegion): number {
  return Object.hasOwn(REGIONAL_MULTIPLIERS, region) ? REGIONAL_MULTIPLIERS[region] : 1.0;
}

/**
 * Calculates the hourly cost for a machine configuration
 */
export function calculateHourlyCost(
  instanceType: AirGPUInstanceType,
  region: AirGPURegion,
  provisioningType: AirGPUProvisioningType = 'on-demand',
  maxUpload: number = 15,
  rootVolumeSize: number = 100
): number {
  // Base rate
  const baseRates = getBaseRates(instanceType);
  const baseRate = provisioningType === 'spot' ? baseRates.spot : baseRates.onDemand;

  // Regional multiplier
  const regionMultiplier = getRegionalMultiplier(region);

  // Upload bandwidth adjustment
  const uploadAdjustment = UPLOAD_BANDWIDTH_ADJUSTMENTS[maxUpload] || 0;

  // Volume size adjustment (for sizes above the included storage)
  const extraVolume = Math.max(0, rootVolumeSize - INCLUDED_VOLUME_GB);
  const volumeAdjustment = extraVolume * VOLUME_PRICING_PER_GB;

  // Total hourly cost
  const totalCost = (baseRate * regionMultiplier) + uploadAdjustment + volumeAdjustment;

  // Round to 4 decimal places
  return Math.round(totalCost * 10000) / 10000;
}

/**
 * Calculates cost for a specific duration
 */
export function calculateUsageCost(
  instanceType: AirGPUInstanceType,
  region: AirGPURegion,
  provisioningType: AirGPUProvisioningType,
  maxUpload: number,
  rootVolumeSize: number,
  durationMinutes: number
): number {
  const hourlyRate = calculateHourlyCost(instanceType, region, provisioningType, maxUpload, rootVolumeSize);
  const hours = durationMinutes / 60;
  const cost = hourlyRate * hours;

  // Round to 2 decimal places for billing
  return Math.round(cost * 100) / 100;
}

/**
 * Estimates cost for a given duration (for pre-authorization)
 */
export function estimateCost(
  instanceType: AirGPUInstanceType,
  region: AirGPURegion,
  provisioningType: AirGPUProvisioningType,
  maxUpload: number = 15,
  rootVolumeSize: number = 100,
  estimatedHours: number = 1
): {
  hourlyCost: number;
  estimatedTotal: number;
  breakdown: {
    baseCost: number;
    regionAdjustment: number;
    uploadAdjustment: number;
    volumeAdjustment: number;
  };
} {
  const baseRates = getBaseRates(instanceType);
  const baseRate = provisioningType === 'spot' ? baseRates.spot : baseRates.onDemand;
  const regionMultiplier = getRegionalMultiplier(region);
  const uploadAdjustment = UPLOAD_BANDWIDTH_ADJUSTMENTS[maxUpload] || 0;
  const extraVolume = Math.max(0, rootVolumeSize - INCLUDED_VOLUME_GB);
  const volumeAdjustment = extraVolume * VOLUME_PRICING_PER_GB;

  const baseCost = baseRate;
  const regionAdjustment = baseRate * (regionMultiplier - 1);
  const hourlyCost = calculateHourlyCost(instanceType, region, provisioningType, maxUpload, rootVolumeSize);
  const estimatedTotal = Math.round(hourlyCost * estimatedHours * 100) / 100;

  return {
    hourlyCost,
    estimatedTotal,
    breakdown: {
      baseCost,
      regionAdjustment: Math.round(regionAdjustment * 10000) / 10000,
      uploadAdjustment,
      volumeAdjustment,
    },
  };
}

/**
 * Formats cost for display
 */
export function formatCost(amount: number, currency: string = 'EUR'): string {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats hourly rate for display
 */
export function formatHourlyRate(amount: number, currency: string = 'EUR'): string {
  return `${formatCost(amount, currency)}/hr`;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Gets the display name for an instance type
 */
export function getInstanceTypeDisplayName(instanceType: AirGPUInstanceType): string {
  const names: Record<AirGPUInstanceType, string> = {
    'g4dn.xlarge': 'Tesla T4 Lite (g4dn.xlarge)',
    'g4dn.2xlarge': 'Tesla T4 (g4dn.2xlarge)',
    'g5.2xlarge': 'A10G (g5.2xlarge)',
    'g6.2xlarge': 'L4 (g6.2xlarge)',
    'g6.4xlarge': 'L4 Pro (g6.4xlarge)',
  };
  return names[instanceType] || instanceType;
}

/**
 * Gets a summary of pricing for an instance type
 */
export function getInstanceTypePricingSummary(instanceType: AirGPUInstanceType): {
  onDemandRange: { min: number; max: number };
  spotRange: { min: number; max: number };
} {
  const baseRates = getBaseRates(instanceType);
  const multipliers = Object.values(REGIONAL_MULTIPLIERS);
  const minMultiplier = Math.min(...multipliers);
  const maxMultiplier = Math.max(...multipliers);

  return {
    onDemandRange: {
      min: Math.round(baseRates.onDemand * minMultiplier * 100) / 100,
      max: Math.round(baseRates.onDemand * maxMultiplier * 100) / 100,
    },
    spotRange: {
      min: Math.round(baseRates.spot * minMultiplier * 100) / 100,
      max: Math.round(baseRates.spot * maxMultiplier * 100) / 100,
    },
  };
}

/**
 * Calculates billing minutes from timestamps
 * Rounds up to the nearest minute (standard cloud billing)
 */
export function calculateBillingMinutes(startTime: Date, endTime: Date): number {
  const durationMs = endTime.getTime() - startTime.getTime();
  const minutes = Math.ceil(durationMs / 60000);
  return Math.max(1, minutes); // Minimum 1 minute
}

/**
 * Calculates minimum required balance for starting a machine.
 * Requires balance for MINIMUM_BALANCE_BUFFER_HOURS (3 hours) of usage.
 * This is a protection against abuse - users can stop anytime and
 * only pay for actual usage (per-minute billing).
 */
export function calculateMinimumRequiredBalance(
  instanceType: AirGPUInstanceType,
  region: AirGPURegion,
  provisioningType: AirGPUProvisioningType = 'on-demand',
  maxUpload: number = 15,
  rootVolumeSize: number = 100,
  bufferHours: number = MINIMUM_BALANCE_BUFFER_HOURS
): number {
  const hourlyRate = calculateHourlyCost(instanceType, region, provisioningType, maxUpload, rootVolumeSize);
  const minimumBalance = hourlyRate * bufferHours;

  // Round up to nearest cent
  return Math.ceil(minimumBalance * 100) / 100;
}
